import { ClientAPI } from '@/redux/api';
import {
  ISearchResultUserInfo,
  LibraryFilters,
  LibraryItem,
  LibraryResponse,
  LocationFieldList,
  ProgramMaterial
} from '@/@types/client-api';
import { NonNullableObjectValues } from '@/utils/types';
import {
  makeDisjunctiveFilterRequest,
  makePrimaryRequest,
  updateGroupData
} from './libraryInternal';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { LIBRARY_FILTERS, LibraryFilterKey, LibraryFilterTypeMap } from '@/utils/libraryConstants';
import { RootState } from '@/redux/store';
import { FilterTypes } from '@/components/ui/SearchResults/constants';
import { toggleLibraryFilter } from '@/redux/slices/librarySlice';
import { getOnlyActiveTabLabel } from '@/utils/libraryUtils';

export type LibraryRequest = NonNullableObjectValues<LibraryFilters>;

export type LibraryResponseProp = {
  items?: Array<LibraryItemProp>;
};

interface LibraryItemProp extends LibraryItem {
  parentSellingPoints?: string;
  userList?: ISearchResultUserInfo[];
}

const normalizeValue = (value?: string | null) => value?.trim().toLowerCase() ?? '';

const isLiveProgramItem = (item: LibraryItemProp) =>
  normalizeValue(String(item.searchableType ?? '')).includes('liveprogram');

const isBookItem = (item: LibraryItemProp) =>
  normalizeValue(String(item.searchableType ?? '')) === 'book';

// Library items for one live program can arrive as separate saved/registered variants.
// Prefer the selected location first, then the current variant's location, so merge logic
// does not accidentally read sibling-variant state from the shared locations array.
const getRelevantLocations = (item: LibraryItemProp) => {
  if (!item.locations?.length) return [];

  const selectedLocations = item.locations.filter(location => location?.isSelected);
  if (selectedLocations.length) return selectedLocations;

  const matchingVariantLocations = item.pk
    ? item.locations.filter(location => location?.pk === item.pk)
    : [];
  if (matchingVariantLocations.length) return matchingVariantLocations;

  return item.locations;
};

const getLocationKey = (location: LocationFieldList) =>
  [
    location.pk ?? '',
    normalizeValue(location.name),
    location.startTimeUtc ?? '',
    location.endTimeUtc ?? ''
  ].join('|');

const getRegistrationKey = (registrationDetails?: LibraryItemProp['registrationDetails']) =>
  registrationDetails
    ? [
        normalizeValue(registrationDetails.format),
        registrationDetails.variationPk ?? '',
        registrationDetails.startDateUtc ?? '',
        registrationDetails.endDateUtc ?? '',
        normalizeValue(registrationDetails.location)
      ].join('|')
    : '';

const getFormatKey = (format: NonNullable<LibraryItemProp['formats']>[number]) =>
  typeof format === 'string'
    ? normalizeValue(format)
    : [format?.pk ?? '', normalizeValue(format?.displayName), normalizeValue(format?.name)].join(
        '|'
      );

// Group sibling live variants into one library snippet by stable program identity plus
// occurrence details. This keeps online/in-person variants for the same event together
// while still allowing distinct dates/locations to render as separate snippets.
const getLibraryGroupKey = (item: LibraryItemProp) => {
  const relevantLocation = getRelevantLocations(item)[0];
  const registrationDetails = item.registrationDetails;

  const programKey =
    item.parentProductGuidValue ??
    item.parentProductCode ??
    item.masterProgram?.guidValue ??
    item.productGuidValue ??
    normalizeValue(item.title) ??
    item.pk ??
    '';

  const start =
    relevantLocation?.startTimeUtc ?? registrationDetails?.startDateUtc ?? item.startTimeUtc ?? '';
  const end =
    relevantLocation?.endTimeUtc ?? registrationDetails?.endDateUtc ?? item.endTimeUtc ?? '';
  const place =
    normalizeValue(relevantLocation?.name) ||
    normalizeValue(registrationDetails?.location) ||
    normalizeValue(item.title);

  return [programKey, start, end, place].join('|');
};

// Centralize the state used to choose the canonical variant for a merged library item.
// This avoids repeating the same purchased/favorite/registration checks across helpers.
const getItemState = (item: LibraryItemProp) => {
  const relevantLocations = getRelevantLocations(item);
  const registrationFormat = normalizeValue(item.registrationDetails?.format);

  return {
    hasPurchased: !!item.isPurchased || relevantLocations.some(location => location?.isPurchased),
    hasFavorite: !!item.isFavorite || relevantLocations.some(location => location?.isFavorite),
    hasWebcastRegistration: registrationFormat.includes('webcast'),
    hasInPersonRegistration: registrationFormat.includes('person'),
    isCurrentlyLiveWebcast:
      relevantLocations.some(
        location =>
          location?.isPurchased &&
          location?.isOnAir &&
          normalizeValue(location?.name).includes('webcast')
      ) ||
      (registrationFormat.includes('webcast') &&
        relevantLocations.some(location => location?.isOnAir))
  };
};

const getRepresentativePriority = (item: LibraryItemProp) => {
  const itemState = getItemState(item);
  if (itemState.isCurrentlyLiveWebcast) return 600;
  if (itemState.hasInPersonRegistration) return 500;
  if (itemState.hasWebcastRegistration) return 400;
  if (itemState.hasPurchased) return 300;
  if (itemState.hasFavorite) return 200;
  return 100;
};

const dedupeByKey = <T>(items: T[], getKey: (item: T) => string) => {
  const seen = new Set<string>();

  return items.filter(item => {
    const key = getKey(item);

    if (!key) {
      return true;
    }

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const toUserListEntry = (item: LibraryItemProp): ISearchResultUserInfo => ({
  pk: item.pk,
  isFavorite: item.isFavorite,
  isPurchased: item.isPurchased || !!item.isParentProgramPurchased,
  isArchived: item.isArchived,
  playbackProgressPct: item.playbackProgressPct,
  currentWatchedSegmentSK: item.currentWatchedSegmentSK,
  price: item.price,
  cleProgressPct: item.cleProgressPct,
  registrationDetails: item.registrationDetails,
  subscriptionExpirationDate: item.subscriptionExpirationDate,
  isPlusExpirationSoon: item.isPlusExpirationSoon,
  hasPlusAccess: item.hasPlusAccess,
  plusExpirationDateFormatted: item.plusExpirationDateFormatted,
  isParentProgramPurchased: item.isParentProgramPurchased,
  hasCreditsForRequest: item.hasCreditsForRequest,
  certificateRequestUrl: item.certificateRequestUrl,
  watchUrl: item.watchUrl,
  addedDateUtc: item.addedDateUtc,
  limitedByContract: item.limitedByContract
});

// Merge sibling live variants into one item so My Library shows a single snippet for a
// program occurrence, while preserving per-variant user state in userList for CTA and
// attendance-format rendering.
const mergeLiveProgramVariants = (items: LibraryItemProp[]): LibraryItemProp => {
  const sortedItems = [...items].sort(
    (left, right) => getRepresentativePriority(right) - getRepresentativePriority(left)
  );
  const representativeItem = sortedItems[0];
  const mergedLocations = dedupeByKey(
    sortedItems.flatMap(item => item.locations ?? []),
    getLocationKey
  );
  const mergedFormats = dedupeByKey(
    sortedItems.flatMap(item => item.formats ?? []),
    getFormatKey
  );
  const mergedUserList = dedupeByKey(sortedItems.map(toUserListEntry), userListItem =>
    [
      userListItem.pk ?? '',
      getRegistrationKey(userListItem.registrationDetails),
      userListItem.watchUrl ?? ''
    ].join('|')
  );

  const webcastItem = sortedItems.find(item => getItemState(item).hasWebcastRegistration);
  const purchasedItem = sortedItems.find(
    item => item.isPurchased || getItemState(item).hasPurchased
  );

  return {
    ...representativeItem,
    pk: representativeItem.pk ?? webcastItem?.pk ?? purchasedItem?.pk ?? items[0]?.pk,
    url: representativeItem.url ?? webcastItem?.url ?? purchasedItem?.url ?? items[0]?.url,
    watchUrl:
      webcastItem?.watchUrl ??
      purchasedItem?.watchUrl ??
      representativeItem.watchUrl ??
      items.find(item => item.watchUrl)?.watchUrl,
    productGuidValue:
      representativeItem.productGuidValue ??
      webcastItem?.productGuidValue ??
      purchasedItem?.productGuidValue ??
      items[0]?.productGuidValue,
    registrationDetails:
      representativeItem.registrationDetails ??
      purchasedItem?.registrationDetails ??
      webcastItem?.registrationDetails,
    formats: mergedFormats,
    locations: mergedLocations,
    isFavorite: sortedItems.some(item => item.isFavorite || getItemState(item).hasFavorite),
    isPurchased: sortedItems.some(item => item.isPurchased || getItemState(item).hasPurchased),
    isUpcomingRegistration: sortedItems.some(item => item.isUpcomingRegistration),
    userList: mergedUserList
  };
};

// The library API can return separate records for online and in-person variants of the
// same live program. Collapse those sibling records into one merged item before the UI
// builds snippets, so saved and registered states stay in sync on a single card.
const consolidateLiveProgramLibraryItems = (items?: LibraryItemProp[]) => {
  if (!items?.length) return items;

  const groupedLiveItems = new Map<string, LibraryItemProp[]>();

  items.forEach(item => {
    if (!isLiveProgramItem(item)) {
      return;
    }

    const groupKey = getLibraryGroupKey(item);

    if (!groupKey) {
      return;
    }

    const currentGroup = groupedLiveItems.get(groupKey) ?? [];
    currentGroup.push(item);
    groupedLiveItems.set(groupKey, currentGroup);
  });

  const handledGroups = new Set<string>();

  return items.flatMap(item => {
    if (!isLiveProgramItem(item)) {
      return [item];
    }

    const groupKey = getLibraryGroupKey(item);
    const groupItems = groupedLiveItems.get(groupKey);

    if (!groupItems?.length) {
      return [item];
    }

    if (handledGroups.has(groupKey)) {
      return [];
    }

    handledGroups.add(groupKey);

    if (groupItems.length === 1) {
      return [item];
    }

    const mergedItem = mergeLiveProgramVariants(groupItems);

    return [mergedItem];
  });
};

const mergeBookVariants = (items: LibraryItemProp[]): LibraryItemProp => {
  const representativeItem = items.find(i => i.isPurchased) ?? items[0];

  const mergedFormats = dedupeByKey(
    items.flatMap(item => item.formats ?? []),
    getFormatKey
  );

  const mergedUserList = dedupeByKey(items.map(toUserListEntry), userListItem =>
    [
      userListItem.pk ?? '',
      getRegistrationKey(userListItem.registrationDetails),
      userListItem.watchUrl ?? ''
    ].join('|')
  );

  return {
    ...representativeItem,
    formats: mergedFormats,
    userList: mergedUserList,
    isPurchased: items.some(i => i.isPurchased),
    isFavorite: items.some(i => i.isFavorite),
    shouldShowOtherBuyingOptions:
      representativeItem.shouldShowOtherBuyingOptions === true &&
      items.every(i => i.shouldShowOtherBuyingOptions !== false)
  };
};

// The library API can return separate records for book variants sharing the same parent
// (e.g. print and one-year e-book subscription). Keep the first purchased variant, or the first item
// if none are purchased, so My Library shows a single snippet per book title.
const consolidateBookLibraryItems = (items?: LibraryItemProp[]) => {
  if (!items?.length) return items;

  const groupedBookItems = new Map<string, LibraryItemProp[]>();

  items.forEach(item => {
    if (!isBookItem(item) || !item.parentProductCode) {
      return;
    }

    const currentGroup = groupedBookItems.get(item.parentProductCode) ?? [];
    currentGroup.push(item);
    groupedBookItems.set(item.parentProductCode, currentGroup);
  });

  const handledGroups = new Set<string>();

  return items.flatMap(item => {
    if (!isBookItem(item) || !item.parentProductCode) {
      return [item];
    }

    const groupKey = item.parentProductCode;
    const groupItems = groupedBookItems.get(groupKey);

    if (!groupItems?.length || groupItems.length === 1) {
      return [item];
    }

    if (handledGroups.has(groupKey)) {
      return [];
    }

    handledGroups.add(groupKey);

    return [mergeBookVariants(groupItems)];
  });
};

const _libraryBasePath = '/user/library';
const libraryEndpoints = ClientAPI.injectEndpoints({
  endpoints: builder => ({
    getLibrary: builder.query<LibraryResponse, LibraryRequest>({
      queryFn: async (requestBody, { dispatch, getState, forced }) => {
        // The primary request to get the search results and available filters
        const primaryRes = await makePrimaryRequest({
          requestBody,
          dispatch,
          forceRefetch: forced
        });

        if (primaryRes.error) return { error: primaryRes.error as FetchBaseQueryError };

        let dataCopy: LibraryResponse;

        try {
          dataCopy = JSON.parse(JSON.stringify(primaryRes.data)) as LibraryResponse;
        } catch (e) {
          return { error: e as FetchBaseQueryError };
        }

        // Normalize live program variants before filters/snippets consume the response.
        dataCopy.items = consolidateLiveProgramLibraryItems(
          dataCopy.items as LibraryItemProp[] | undefined
        ) as LibraryItem[] | undefined;

        // Consolidate book variants sharing the same parentProductCode into one item.
        dataCopy.items = consolidateBookLibraryItems(
          dataCopy.items as LibraryItemProp[] | undefined
        ) as LibraryItem[] | undefined;

        if (
          dataCopy.items?.length === 0 &&
          dataCopy.summary?.filtersApplied?.groupSearchableType !== 'All'
        ) {
          const currentTabLabel = new URLSearchParams(window.location.search).get(
            'groupSearchableType'
          );
          const tabData = Object.entries(
            dataCopy?.summary?.headerGroupSearchableTypeCounters || []
          );
          const activeTabs = tabData.filter(([_, count]) => count);

          const activeTab = getOnlyActiveTabLabel(dataCopy);
          const tab =
            !activeTab && currentTabLabel && activeTabs.some(([label]) => label === currentTabLabel)
              ? currentTabLabel
              : (activeTab ?? 'All');

          dispatch(
            toggleLibraryFilter({
              filterId: LIBRARY_FILTERS.GROUP_SEARCHABLE_TYPE,
              value: tab
            })
          );
          libraryEndpoints.util.invalidateTags([{ type: 'Library', id: 'List' }]);
        }

        const filters = Object.entries(dataCopy.summary?.filtersApplied ?? []);

        // The list of disjunctive filters that need their own query
        const disjunctiveFiltersThatNeedRequests = filters.filter(([name, value]) => {
          const type = LibraryFilterTypeMap[name];
          return [FilterTypes.RADIO, FilterTypes.SELECT].includes(type) && value;
        });

        const newFilterGroups = await Promise.all(
          disjunctiveFiltersThatNeedRequests.map(([name, value]) =>
            makeDisjunctiveFilterRequest({
              filter: { name: name as LibraryFilterKey, value: value as string },
              requestBody,
              getState: getState as () => RootState,
              dispatch
            })
          )
        );

        newFilterGroups.forEach(newGroup =>
          updateGroupData({
            newGroup,
            dataToUpdate: dataCopy
          })
        );

        return { data: dataCopy };
      },
      providesTags: () => [{ type: 'Library', id: 'List' }]
    }),
    getFullLibrary: builder.query<LibraryResponse, void>({
      query: () => ({
        url: `${_libraryBasePath}?includeAllSegments=true&includeArchived=true`,
        method: 'GET'
      })
    }),
    getLibraryItemsByPks: builder.query<LibraryItem[], string[]>({
      query: itemPks => ({
        url: `${_libraryBasePath}/by-itemPks`,
        method: 'GET',
        params: {
          includeAllSegments: true,
          includeArchived: true,
          includeFavorites: true,
          fetchCreditRequestInfo: false,
          includeNested: true,
          itemPks: itemPks,
          noCache: true,
          returnNonSupportedItems: false,
          resolveContent: true,
          fetchCreditPreferences: false,
          fetchUpcomingRegistrations: true,
          includeLiveToOnDemand: false
        }
      }),
      providesTags: (_, __, args) => args.map(itemPk => ({ type: 'LibraryItem', id: itemPk }))
    }),
    getLibraryUpcomingRegistrations: builder.query<void, { dateRange: string } | void>({
      query: params => ({
        url: `/search?MyLibrary=Upcoming&FilterGroupsToReturn=MyLibrary`,
        method: 'GET',
        params: params ? { dateRange: params.dateRange } : undefined
      })
    }),
    getLibrarySegments: builder.query<LibraryItem[], string>({
      query: code => ({
        url: `${_libraryBasePath}/${code}/segments`,
        method: 'GET'
      })
    }),
    getProgramMaterials: builder.query<ProgramMaterial[], string>({
      query: code => ({
        url: `${_libraryBasePath}/${code}/materials`,
        method: 'GET'
      })
    }),
    getPoecProgramsForRenewal: builder.query<LibraryItem[], void>({
      query: () => ({
        url: `${_libraryBasePath}/poec-renewal`,
        method: 'GET'
      }),
      providesTags: ['POECRenewalItems'],
      keepUnusedDataFor: 0
    }),
    addToLibrary: builder.mutation<boolean, string>({
      query: code => ({
        url: `${_libraryBasePath}/${code}`,
        param: `${code}`,
        method: 'POST'
      }),
      invalidatesTags: (_result, _error, code) => [
        { type: 'LibraryItem', id: code },
        { type: 'Library', id: 'List' }
      ]
    }),
    removeFromLibrary: builder.mutation<boolean, string>({
      query: code => ({
        url: `${_libraryBasePath}/${code}`,
        param: `${code}`,
        method: 'DELETE'
      }),
      invalidatesTags: (_result, _error, code) => [
        { type: 'LibraryItem', id: code },
        { type: 'Library', id: 'List' }
      ]
    })
  })
});

export const {
  useGetLibraryQuery,
  useGetFullLibraryQuery,
  useLazyGetFullLibraryQuery,
  useLazyGetLibraryItemsByPksQuery,
  useLazyGetLibrarySegmentsQuery,
  useGetLibrarySegmentsQuery,
  useGetProgramMaterialsQuery,
  useGetPoecProgramsForRenewalQuery,
  useLazyGetProgramMaterialsQuery,
  useAddToLibraryMutation,
  useRemoveFromLibraryMutation,
  useGetLibraryUpcomingRegistrationsQuery
} = libraryEndpoints;

export default libraryEndpoints;
